import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, ChevronDown, ChevronRight, BarChart3, Timer, Users, Layers, Settings2 } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * AnalyticsPanel — Developer-only insights into how iPad guests and crew
 * accounts actually use the app.
 *
 * Two tables (iPad + Crew) plus 10 KPIs at the top, filterable by
 *   • All time
 *   • Current charter
 *   • Specific charter (dropdown from /admin/charters)
 *
 * Retention/compress settings live inline so the Developer can tweak
 * without hopping to Settings — writes go to /admin/analytics/settings.
 */

function fmtMs(ms) {
  if (!ms || ms < 1000) return `${ms || 0} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  const m = s / 60;
  if (m < 60) return `${m.toFixed(1)} min`;
  const h = m / 60;
  return `${h.toFixed(1)} h`;
}

function fmtWhen(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AnalyticsPanel() {
  const [scope, setScope] = useState("all");
  const [charters, setCharters] = useState([]);
  const [charterId, setCharterId] = useState("");
  const [kpis, setKpis] = useState([]);
  const [ipads, setIpads] = useState([]);
  const [crew, setCrew] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandActor, setExpandActor] = useState(null);
  const [drill, setDrill] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [compressing, setCompressing] = useState(false);

  const params = useMemo(() => {
    const p = { scope };
    if (scope === "charter" && charterId) p.charter_id = charterId;
    return p;
  }, [scope, charterId]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [k, ipad, cr] = await Promise.all([
        api.get("/admin/analytics/kpis", { params }),
        api.get("/admin/analytics/summary", { params: { ...params, kind: "ipad" } }),
        api.get("/admin/analytics/summary", { params: { ...params, kind: "dashboard" } }),
      ]);
      setKpis(k.data?.kpis || []);
      setIpads(ipad.data?.actors || []);
      setCrew(cr.data?.actors || []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([
          api.get("/admin/charters"),
          api.get("/admin/analytics/settings"),
        ]);
        setCharters(c.data || []);
        setSettings(s.data || {});
      } catch { /* silent — settings/charters best-effort */ }
    })();
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openDrill = async (actor) => {
    if (expandActor === actor.actor_id) { setExpandActor(null); setDrill(null); return; }
    setExpandActor(actor.actor_id);
    setDrill(null);
    try {
      const r = await api.get(`/admin/analytics/actor/${actor.actor_id}`, { params });
      setDrill({ actor_id: actor.actor_id, events: r.data?.events || [] });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to load drill-down");
    }
  };

  const saveSettings = async (patch) => {
    setSavingSettings(true);
    try {
      const r = await api.put("/admin/analytics/settings", patch);
      setSettings(r.data || settings);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to save");
    } finally { setSavingSettings(false); }
  };

  const runCompress = async () => {
    setCompressing(true);
    try {
      const r = await api.post("/admin/analytics/compress");
      toast.success(`Compressed ${r.data?.buckets_written || 0} buckets · ${r.data?.raw_rows_deleted || 0} rows pruned`);
      loadAll();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to compress");
    } finally { setCompressing(false); }
  };

  return (
    <section className="border border-border p-6" data-testid="analytics-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-2xl flex items-center gap-2"><BarChart3 className="h-5 w-5" /> Feature Analytics</h3>
        <Button variant="outline" size="sm" onClick={loadAll} disabled={loading} data-testid="an-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <Label className="overline text-primary/60 text-[10px]">Time scope</Label>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-52 mt-1" data-testid="an-scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              <SelectItem value="current">Current charter</SelectItem>
              <SelectItem value="charter">Specific charter…</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {scope === "charter" && (
          <div>
            <Label className="overline text-primary/60 text-[10px]">Charter</Label>
            <Select value={charterId} onValueChange={setCharterId}>
              <SelectTrigger className="w-72 mt-1" data-testid="an-charter"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {charters.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name || c.title || c.id.slice(-6)} · {c.arrival?.slice(0, 10) || "?"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 mb-8" data-testid="an-kpis">
        {kpis.map((k) => (
          <div key={k.key} className="border border-border p-3 bg-background" data-testid={`an-kpi-${k.key}`}>
            <div className="overline text-primary/50 text-[10px] mb-1">{k.label}</div>
            <div className="font-display text-lg truncate">{k.value ?? "—"}</div>
            <div className="text-[10px] text-muted-foreground truncate mt-0.5">{k.hint}</div>
          </div>
        ))}
      </div>

      {/* iPad table */}
      <div className="mb-8" data-testid="an-ipad-table">
        <h4 className="font-display text-lg flex items-center gap-2 mb-3"><Layers className="h-4 w-4" /> iPads / Cabin devices</h4>
        {ipads.length === 0 && <p className="text-sm text-muted-foreground">No iPad events in range.</p>}
        {ipads.length > 0 && (
          <div className="border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="text-left p-2">Cabin / actor</th>
                  <th className="text-right p-2">Total time</th>
                  <th className="text-right p-2">Views</th>
                  <th className="text-right p-2">Actions</th>
                  <th className="text-left p-2">Top feature</th>
                  <th className="text-left p-2">Last seen</th>
                  <th className="p-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {ipads.map((a) => (
                  <React.Fragment key={a.actor_id}>
                    <tr className="border-t border-border hover:bg-muted/20 cursor-pointer" onClick={() => openDrill(a)} data-testid={`an-ipad-row-${a.actor_id}`}>
                      <td className="p-2 truncate max-w-[240px]">{a.cabin_name || <span className="text-muted-foreground">Guest device · {a.actor_id.slice(-6)}</span>}</td>
                      <td className="p-2 text-right font-mono">{fmtMs(a.total_ms)}</td>
                      <td className="p-2 text-right font-mono">{a.total_visits}</td>
                      <td className="p-2 text-right font-mono">{a.total_actions}</td>
                      <td className="p-2 truncate max-w-[160px]">{a.features?.[0]?.feature || "—"}</td>
                      <td className="p-2 whitespace-nowrap text-muted-foreground">{fmtWhen(a.last_at)}</td>
                      <td className="p-2 text-right">{expandActor === a.actor_id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                    </tr>
                    {expandActor === a.actor_id && (
                      <tr className="bg-muted/10">
                        <td colSpan={7} className="p-3">
                          <div className="grid gap-2 grid-cols-1 md:grid-cols-2">
                            <div>
                              <div className="overline text-primary/60 text-[10px] mb-1">Feature breakdown</div>
                              <ul className="text-xs space-y-1">
                                {(a.features || []).map((f) => (
                                  <li key={f.feature} className="flex items-center justify-between gap-3 border-b border-border/50 py-1">
                                    <span className="truncate">{f.feature}</span>
                                    <span className="font-mono text-muted-foreground">{fmtMs(f.total_ms)} · {f.visits} views · {f.actions} actions</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <div className="overline text-primary/60 text-[10px] mb-1">Recent events</div>
                              {!drill && <p className="text-xs text-muted-foreground">Loading…</p>}
                              {drill && drill.events.length === 0 && <p className="text-xs text-muted-foreground">No recent events.</p>}
                              {drill && drill.events.length > 0 && (
                                <ul className="text-[11px] font-mono space-y-0.5 max-h-72 overflow-auto">
                                  {drill.events.slice(0, 60).map((ev, i) => (
                                    <li key={i} className="truncate">
                                      <span className="text-muted-foreground">{ev.at?.slice(11, 19)}</span> · {ev.feature} · {ev.action}{ev.meta?.event ? `:${ev.meta.event}` : ""}{ev.duration_ms ? ` · ${fmtMs(ev.duration_ms)}` : ""}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Crew table */}
      <div className="mb-8" data-testid="an-crew-table">
        <h4 className="font-display text-lg flex items-center gap-2 mb-3"><Users className="h-4 w-4" /> Crew accounts</h4>
        {crew.length === 0 && <p className="text-sm text-muted-foreground">No dashboard events in range.</p>}
        {crew.length > 0 && (
          <div className="border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="text-left p-2">User</th>
                  <th className="text-left p-2">Role</th>
                  <th className="text-right p-2">Total time</th>
                  <th className="text-right p-2">Views</th>
                  <th className="text-right p-2">Actions</th>
                  <th className="text-left p-2">Top panel</th>
                  <th className="text-left p-2">Last seen</th>
                  <th className="p-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {crew.map((a) => (
                  <React.Fragment key={a.actor_id}>
                    <tr className="border-t border-border hover:bg-muted/20 cursor-pointer" onClick={() => openDrill(a)} data-testid={`an-crew-row-${a.actor_id}`}>
                      <td className="p-2 truncate max-w-[220px]">{a.user_name || a.actor_id}</td>
                      <td className="p-2">{a.user_role || "—"}</td>
                      <td className="p-2 text-right font-mono">{fmtMs(a.total_ms)}</td>
                      <td className="p-2 text-right font-mono">{a.total_visits}</td>
                      <td className="p-2 text-right font-mono">{a.total_actions}</td>
                      <td className="p-2 truncate max-w-[180px]">{a.features?.[0]?.feature || "—"}</td>
                      <td className="p-2 whitespace-nowrap text-muted-foreground">{fmtWhen(a.last_at)}</td>
                      <td className="p-2 text-right">{expandActor === a.actor_id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                    </tr>
                    {expandActor === a.actor_id && (
                      <tr className="bg-muted/10">
                        <td colSpan={8} className="p-3">
                          <div className="grid gap-2 grid-cols-1 md:grid-cols-2">
                            <div>
                              <div className="overline text-primary/60 text-[10px] mb-1">Panel breakdown</div>
                              <ul className="text-xs space-y-1">
                                {(a.features || []).map((f) => (
                                  <li key={f.feature} className="flex items-center justify-between gap-3 border-b border-border/50 py-1">
                                    <span className="truncate">{f.feature}</span>
                                    <span className="font-mono text-muted-foreground">{fmtMs(f.total_ms)} · {f.visits} views · {f.actions} actions</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <div className="overline text-primary/60 text-[10px] mb-1">Recent events</div>
                              {!drill && <p className="text-xs text-muted-foreground">Loading…</p>}
                              {drill && drill.events.length > 0 && (
                                <ul className="text-[11px] font-mono space-y-0.5 max-h-72 overflow-auto">
                                  {drill.events.slice(0, 60).map((ev, i) => (
                                    <li key={i} className="truncate">
                                      <span className="text-muted-foreground">{ev.at?.slice(11, 19)}</span> · {ev.feature} · {ev.action}{ev.meta?.event ? `:${ev.meta.event}` : ""}{ev.duration_ms ? ` · ${fmtMs(ev.duration_ms)}` : ""}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Compression settings */}
      <div className="border border-border p-4 bg-muted/10" data-testid="an-settings">
        <h4 className="font-display text-base flex items-center gap-2 mb-3"><Settings2 className="h-4 w-4" /> Retention & compression</h4>
        {!settings && <p className="text-xs text-muted-foreground">Loading…</p>}
        {settings && (
          <div className="flex flex-wrap items-end gap-4">
            <label className="flex items-center gap-2">
              <Switch
                checked={!!settings.analytics_enabled}
                onCheckedChange={(v) => saveSettings({ analytics_enabled: v })}
                disabled={savingSettings}
                data-testid="an-toggle-enabled"
              />
              <span className="text-sm">Analytics enabled</span>
            </label>
            <label className="flex items-center gap-2">
              <Switch
                checked={!!settings.analytics_compress_enabled}
                onCheckedChange={(v) => saveSettings({ analytics_compress_enabled: v })}
                disabled={savingSettings}
                data-testid="an-toggle-compress"
              />
              <span className="text-sm">Weekly compression</span>
            </label>
            <div>
              <Label className="overline text-primary/60 text-[10px]">Retention (raw days)</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={settings.analytics_retention_days || 30}
                onChange={(e) => setSettings({ ...settings, analytics_retention_days: e.target.value })}
                onBlur={(e) => saveSettings({ analytics_retention_days: parseInt(e.target.value, 10) || 30 })}
                className="w-24 mt-1"
                data-testid="an-retention"
              />
            </div>
            <Button size="sm" variant="outline" onClick={runCompress} disabled={compressing} data-testid="an-run-compress">
              <Timer className={`h-3.5 w-3.5 mr-1 ${compressing ? "animate-spin" : ""}`} />
              Compress now
            </Button>
          </div>
        )}
        <p className="text-[11px] text-muted-foreground mt-3">
          Raw events older than the retention window are rolled up into weekly buckets keyed by actor+feature+charter, then the raw rows are pruned. Charter-scoped buckets stay forever so per-trip drill-downs remain available.
        </p>
      </div>
    </section>
  );
}
